import { lazy, Suspense } from 'react';
import { api, uploadAttachment } from '../api/client';
import { useDraft } from '../lib/chatDrafts';
import { commentAttachmentIds, readVideoComments, videoCommentFrames, type VideoComment, type VideoCommentDraft, type VideoFrameDraft } from '../lib/videoComments';
import { persistVideoCommentGroup, serializeVideoCommentsForSave, singleFrameEdit, unreferencedCommentAttachments } from '../lib/persistVideoComment';
import type { MediaSource } from '../lib/mediaReview';
import type { Attachment } from '../api/types';
import { Modal } from './Modal';
const VideoReview = lazy(() => import('./VideoReview'));

function historyVideoHtml(source: MediaSource, comments: VideoComment[]) {
  const video = document.createElement('video');
  video.src = source.url; video.controls = true; video.preload = 'metadata';
  video.dataset.filename = source.name; video.dataset.videoReview = 'true';
  if (source.attachmentId) video.dataset.attachmentId = String(source.attachmentId);
  video.dataset.videoComments = serializeVideoCommentsForSave(comments);
  return video.outerHTML;
}

export type ReviewFile = { id?: number; attachmentUrl: string; name: string };

/** A new review of a historical video owns only newly uploaded annotations.
 * Original video and marks are borrowed and must never be deleted/replaced. */
export function HistoryVideoReview({ source, draftKey, canSend, onSend, onClose, stateId }: {
  source: MediaSource; draftKey: string; canSend: boolean; stateId?: number;
  onSend: (html: string, files: ReviewFile[], owned: Attachment[]) => void | Promise<void>; onClose: () => void;
}) {
  const { message, attachments, setMessage, setAttachments } = useDraft(`history-video:${draftKey}:${source.url}`);
  const container = document.createElement('div'); container.innerHTML = message;
  const comments = message ? readVideoComments(container.querySelector('video')?.dataset.videoComments) : source.comments || [];
  const update = (next: VideoComment[]) => setMessage(historyVideoHtml(source, next));
  const saveGroup = async (frames: VideoFrameDraft[], comment: VideoCommentDraft, previous?: VideoComment, voice?: File | null) => {
    const { saved, obsoleteIds } = await persistVideoCommentGroup(frames, comment, previous, voice, {
      upload: file => uploadAttachment(file, { stateId }),
      rollback: async uploaded => {
        for (const file of uploaded) {
          try { await api.deleteAttachment(file.deleteUrl); }
          catch { setAttachments(current => current.some(item => item.id === file.id) ? current : [...current, file]); }
        }
      },
      commit: (saved, uploaded) => {
        update([...comments.filter(item => item.id !== saved.id), saved].sort((a, b) => a.time - b.time || a.id.localeCompare(b.id)));
        setAttachments(current => [...current, ...uploaded]);
      },
    });
    const retained = new Set([...comments.filter(item => item.id !== saved.id).flatMap(commentAttachmentIds), ...commentAttachmentIds(saved), source.attachmentId]);
    for (const id of obsoleteIds.filter(id => !retained.has(id))) {
      // Only our unsent uploads are owned. Original images/recordings are borrowed.
      const owned = attachments.find(item => item.id === id);
      if (!owned) continue;
      try { await api.deleteAttachment(owned.deleteUrl); setAttachments(current => current.filter(item => item.id !== id)); }
      catch { /* Retain failed-cleanup ownership for a later retry. */ }
    }
    return saved;
  };
  return <Suspense fallback={<Modal title="Видео" onClose={onClose}>Открываем…</Modal>}><VideoReview source={{ ...source, comments }} onClose={onClose} draft={{
    canSend: canSend && Boolean(message),
    save: (file, comment, previous, voice) => saveGroup(singleFrameEdit(file, comment, previous), comment, previous, voice),
    saveGroup,
    remove: async comment => {
      // Unlink first. A failed second deletion must not leave a visible card
      // whose image has already been removed. Retain failed cleanup ownership.
      update(comments.filter(item => item.id !== comment.id));
      for (const id of unreferencedCommentAttachments(comment, comments.filter(item => item.id !== comment.id), source.attachmentId ? [source.attachmentId] : [])) {
        const owned = attachments.find(item => item.id === id);
        if (owned) {
          try { await api.deleteAttachment(owned.deleteUrl); setAttachments(current => current.filter(item => item.id !== owned.id)); }
          catch { /* A later successful send retries cleanup of unused files. */ }
        }
      }
    },
    send: async () => {
      const files: ReviewFile[] = [{id: source.attachmentId, attachmentUrl: source.url, name: source.name},
        ...comments.flatMap(item => [...videoCommentFrames(item).map(frame => ({id:frame.attachmentId, attachmentUrl:frame.imageUrl, name:`${source.name}-кадр-${frame.frame || frame.time}.png`})),...(item.voiceAttachmentId && item.voiceUrl ? [{id:item.voiceAttachmentId,attachmentUrl:item.voiceUrl,name:'Голосовой комментарий'}] : [])])];
      const uniqueFiles = files.filter((file, index) => files.findIndex(other => file.id !== undefined ? other.id === file.id : other.attachmentUrl === file.attachmentUrl) === index);
      const referenced = new Set(comments.flatMap(commentAttachmentIds));
      await onSend(historyVideoHtml(source, comments), uniqueFiles, attachments.filter(item => referenced.has(item.id)));
      await Promise.allSettled(attachments.filter(item => !referenced.has(item.id)).map(item => api.deleteAttachment(item.deleteUrl)));
      setMessage(''); setAttachments([]);
    },
  }}/></Suspense>;
}
